import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Table from '../../components/ui/Table';
import Pagination from '../../components/ui/Pagination';
import { Icon } from '../../components/ui/Icon';

import { fetchAudit, setFilter, resetFilters } from '../../store/slices/auditSlice';
import { formatDateTime } from '../../utils/format';

// Renk eşlemesi — action tipine göre badge rengi
const ACTION_COLOR = {
  create:       'green',
  update:       'blue',
  delete:       'red',
  activate:     'teal',
  close:        'neutral',
  evaluate:     'purple',
  cancel:       'orange',
  rollback:     'red',
  sync:         'blue',
  manual_score: 'yellow',
  upload:       'green',
};

const ENTITY_OPTIONS = [
  { value: '', label: 'Tüm varlıklar' },
  { value: 'task',            label: 'Görev' },
  { value: 'period',          label: 'Dönem' },
  { value: 'parameter',       label: 'Parametre' },
  { value: 'evaluation_job',  label: 'Hesaplama' },
  { value: 'user_registry',   label: 'Kullanıcı' },
  { value: 'badge',           label: 'Rozet' },
];

const ACTION_OPTIONS = [
  { value: '', label: 'Tüm aksiyonlar' },
  { value: 'create',       label: 'Oluştur' },
  { value: 'update',       label: 'Güncelle' },
  { value: 'delete',       label: 'Sil' },
  { value: 'activate',     label: 'Aktifleştir' },
  { value: 'close',        label: 'Kapat' },
  { value: 'evaluate',     label: 'Hesapla' },
  { value: 'evaluate_all', label: 'Toplu Hesapla' },
  { value: 'cancel',       label: 'İptal' },
  { value: 'rollback',     label: 'Geri Al' },
  { value: 'sync',         label: 'Senkronize' },
  { value: 'manual_score', label: 'Manuel Skor' },
  { value: 'upload',       label: 'Yükle' },
];

function DiffViewer({ diff }) {
  const [open, setOpen] = useState(false);
  if (!diff || Object.keys(diff).length === 0)
    return <span className="text-neutral-400 text-xs">—</span>;

  const displayDiff = Object.fromEntries(
    Object.entries(diff).filter(([k]) => k !== '_meta'),
  );

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-primary-600 transition-colors"
        title={open ? 'Gizle' : 'Detayı gör'}
      >
        <Icon name={open ? 'eyeOff' : 'eye'} className="h-4 w-4" />
      </button>
      {open && (
        <pre className="mt-1 text-xs bg-neutral-50 border border-neutral-200 rounded p-2 max-w-xs overflow-x-auto">
          {JSON.stringify(displayDiff, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function Audit() {
  const dispatch = useDispatch();
  const { items, status, filters, pagination } = useSelector((s) => s.audit);
  const [page, setPage] = useState(1);

  const load = (p = page) => {
    const params = { page: p, size: 50 };
    if (filters.entity) params.entity = filters.entity;
    if (filters.action) params.action = filters.action;
    if (filters.admin_username.trim()) params.admin_username = filters.admin_username.trim();
    dispatch(fetchAudit(params));
  };

  useEffect(() => { load(1); setPage(1); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = () => {
    dispatch(resetFilters());
    setPage(1);
  };

  const isLoading = status === 'loading';

  const columns = [
    {
      key: 'ts',
      header: (
        <span className="flex items-center gap-1.5">
          <Icon name="clock" className="h-4 w-4 text-neutral-400" />
          Zaman
        </span>
      ),
      render: (row) => (
        <span className="whitespace-nowrap text-neutral-500 text-xs">
          {row.ts ? formatDateTime(row.ts) : '—'}
        </span>
      ),
    },
    {
      key: 'action',
      header: (
        <span className="flex items-center gap-1.5">
          <Icon name="zap" className="h-4 w-4 text-neutral-400" />
          Aksiyon
        </span>
      ),
      render: (row) => (
        <Badge color={ACTION_COLOR[row.action] || 'neutral'} size="sm">
          {row.action}
        </Badge>
      ),
    },
    {
      key: 'entity',
      header: (
        <span className="flex items-center gap-1.5">
          <Icon name="layers" className="h-4 w-4 text-neutral-400" />
          Varlık
        </span>
      ),
      render: (row) => (
        <span className="text-xs font-mono text-neutral-700">
          {row.entity}
          {row.entity_id && <span className="text-neutral-400"> #{row.entity_id}</span>}
        </span>
      ),
    },
    {
      key: 'resource_name',
      header: (
        <span className="flex items-center gap-1.5">
          <Icon name="fileText" className="h-4 w-4 text-neutral-400" />
          Kaynak
        </span>
      ),
      render: (row) => (
        <span className="text-xs text-neutral-700 max-w-[160px] truncate block" title={row.resource_name}>
          {row.resource_name || '—'}
        </span>
      ),
    },
    {
      key: 'admin_username',
      header: (
        <span className="flex items-center gap-1.5">
          <Icon name="user" className="h-4 w-4 text-neutral-400" />
          Admin
        </span>
      ),
      render: (row) => (
        <span className="text-xs text-neutral-600">
          {row.admin_username || row.user_id || '—'}
        </span>
      ),
    },
    {
      key: 'ip',
      header: (
        <span className="flex items-center gap-1.5">
          <Icon name="monitor" className="h-4 w-4 text-neutral-400" />
          IP
        </span>
      ),
      render: (row) => (
        <span className="text-xs font-mono text-neutral-400">{row.ip || '—'}</span>
      ),
    },
    {
      key: 'diff',
      header: (
        <span className="flex items-center gap-1.5">
          <Icon name="code" className="h-4 w-4 text-neutral-400" />
          Detay
        </span>
      ),
      render: (row) => <DiffViewer diff={row.diff} />,
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Denetim Günlüğü"
        subtitle="Tüm admin aksiyonlarının kaydı"
        className="bg-white border border-[#E1E1E1] shadow-card rounded-[1.25rem] px-6 py-4"
      />

      <Card padded={false}>
        {/* Filtreler */}
        <div className="flex flex-wrap gap-3 items-end p-4">
          <div className="w-44">
            <Select
              label="Varlık"
              value={filters.entity}
              onChange={(e) => dispatch(setFilter({ entity: e.target.value }))}
              options={ENTITY_OPTIONS}
            />
          </div>
          <div className="w-44">
            <Select
              label="Aksiyon"
              value={filters.action}
              onChange={(e) => dispatch(setFilter({ action: e.target.value }))}
              options={ACTION_OPTIONS}
            />
          </div>
          <div className="w-64">
            {/* hint prop kaldırıldı — Varlık/Aksiyon select'lerinde olmadığı
                için üç input'un dikey hizasını bozuyordu (items-end alt-kenar
                hizalaması hint yüksekliği kadar Admin input'unu yukarı
                kaydırıyordu). Açıklama placeholder'a entegre edildi:
                "Ad Soyad, e-posta veya user_id (UUID)" — kullanıcıya hangi
                alanlarda arama yapıldığı tek satırda iletilir. */}
            <Input
              label="Admin"
              placeholder="Ad Soyad, e-posta veya user_id (UUID)"
              value={filters.admin_username}
              onChange={(e) => dispatch(setFilter({ admin_username: e.target.value }))}
              className="h-11"
            />
          </div>
          <Button variant="secondary" onClick={handleReset} className="h-11 px-4">
            Temizle
          </Button>
        </div>

        <Table
          columns={columns}
          rows={items}
          loading={isLoading}
          getRowKey={(r) => r.id}
          emptyMessage="Kayıt bulunamadı."
        />

        {pagination.total > 0 && (
          <div className="px-4 pb-4 pt-2 flex justify-between items-center text-xs text-neutral-500 border-t border-neutral-200">
            <span>{pagination.total} kayıt</span>
            <Pagination
              page={pagination.page}
              total={pagination.total}
              size={pagination.size}
              onPageChange={(p) => setPage(p)}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
